import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useState, useEffect } from "react";
import { List, Loader2, Plus, Minus } from "lucide-react";

interface CreateRuleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (ruleData: any) => Promise<void>;
  editingRule?: any;
}

export function CreateRuleModal({
  isOpen,
  onClose,
  onSave,
  editingRule,
}: CreateRuleModalProps) {
  const [formData, setFormData] = useState({
    rule_name: "",
    description: "",
    price_modifier: "",
    price_type: "fixed",
    is_required: false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [operation, setOperation] = useState<"add" | "subtract">("add");

  useEffect(() => {
    if (isOpen && editingRule) {
      const modifier = parseFloat(editingRule.price_modifier.toString());
      setOperation(modifier < 0 ? "subtract" : "add");
      setFormData({
        rule_name: editingRule.rule_name,
        description: editingRule.description || "",
        price_modifier: Math.abs(modifier).toString(),
        price_type: editingRule.price_type,
        is_required: editingRule.is_required,
      });
    } else if (isOpen && !editingRule) {
      resetForm();
    }
  }, [isOpen, editingRule]);

  const resetForm = () => {
    setFormData({
      rule_name: "",
      description: "",
      price_modifier: "",
      price_type: "fixed",
      is_required: false,
    });
    setOperation("add");
    setErrors({});
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.rule_name.trim()) {
      newErrors.rule_name = "El nombre es obligatorio";
    }

    if (!formData.price_modifier) {
      newErrors.price_modifier = "El modificador de precio es obligatorio";
    } else if (isNaN(parseFloat(formData.price_modifier))) {
      newErrors.price_modifier = "Debe ser un número válido";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setIsLoading(true);
    try {
      let modifierValue = parseFloat(formData.price_modifier);
      if (operation === "subtract") {
        modifierValue = -Math.abs(modifierValue);
      } else {
        modifierValue = Math.abs(modifierValue);
      }

      const payload: any = {
        ...formData,
        price_modifier: modifierValue,
      };

      if (!editingRule) {
        payload.order_position = 0;
      }

      await onSave(payload);
      handleClose();
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="w-[95vw] sm:max-w-lg max-h-[90vh] overflow-hidden p-0 custom-scrollbar rounded-[24px] border-slate-100 flex flex-col">
        <form onSubmit={handleSubmit} className="flex flex-col h-full overflow-hidden">
          
          {/* Header */}
          <div className="bg-white border-b border-slate-100 p-3 sm:px-5 sm:py-3 z-10 relative flex-shrink-0">
            <DialogHeader>
              <div className="flex items-center space-x-3">
                <div className="bg-indigo-100/80 p-2.5 rounded-xl">
                  <List className="h-5 w-5 text-indigo-600" />
                </div>
                <div className="text-left">
                  <DialogTitle className="text-base font-bold text-slate-900 tracking-tight">
                    {editingRule ? "Editar Ajuste de Precio" : "Nuevo Ajuste de Precio"}
                  </DialogTitle>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {editingRule ? "Modifica los valores del ajuste" : "Crea una regla de aumento o descuento"}
                  </p>
                </div>
              </div>
            </DialogHeader>
          </div>

          {/* Body */}
          <div className="p-4 sm:p-5 overflow-y-auto custom-scrollbar flex-1 bg-slate-50/50">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="rule_name" className="text-sm font-medium text-slate-700">Nombre del Ajuste *</Label>
                <Input
                  id="rule_name"
                  value={formData.rule_name}
                  onChange={(e) => handleChange("rule_name", e.target.value)}
                  placeholder="Ej: Descuento por volumen"
                  className={`mt-1 h-9 rounded-lg bg-white border-slate-200 hover:border-slate-300 focus-visible:ring-indigo-500 focus-visible:border-indigo-500 transition-all shadow-sm ${
                    errors.rule_name ? "border-red-500 focus-visible:ring-red-500 bg-red-50/50" : ""
                  }`}
                />
                {errors.rule_name && (
                  <p className="text-sm text-red-500 mt-1">{errors.rule_name}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-700">Tipo de Ajuste</Label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant={operation === "add" ? "default" : "outline"}
                    className={`flex-1 rounded-xl h-9 transition-all text-sm ${
                      operation === "add"
                        ? "bg-emerald-600 hover:bg-emerald-700 text-white shadow-md shadow-emerald-600/20"
                        : "hover:bg-emerald-50 text-emerald-700 border-emerald-200/60"
                    }`}
                    onClick={() => setOperation("add")}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Aumento (+)
                  </Button>
                  <Button
                    type="button"
                    variant={operation === "subtract" ? "default" : "outline"}
                    className={`flex-1 rounded-xl h-9 transition-all text-sm ${
                      operation === "subtract"
                        ? "bg-rose-600 hover:bg-rose-700 text-white shadow-md shadow-rose-600/20"
                        : "hover:bg-rose-50 text-rose-700 border-rose-200/60"
                    }`}
                    onClick={() => setOperation("subtract")}
                  >
                    <Minus className="h-4 w-4 mr-2" />
                    Descuento (-)
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="price_modifier" className="text-sm font-medium text-slate-700">Valor *</Label>
                  <Input
                    id="price_modifier"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.price_modifier}
                    onChange={(e) => handleChange("price_modifier", e.target.value)}
                    placeholder="0.00"
                    className={`mt-1 h-9 rounded-lg bg-white border-slate-200 hover:border-slate-300 focus-visible:ring-indigo-500 focus-visible:border-indigo-500 transition-all shadow-sm ${
                      errors.price_modifier ? "border-red-500 focus-visible:ring-red-500 bg-red-50/50" : ""
                    }`}
                  />
                  {errors.price_modifier && (
                    <p className="text-sm text-red-500 mt-1">{errors.price_modifier}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="price_type" className="text-sm font-medium text-slate-700">Tipo de Valor</Label>
                  <Select
                    value={formData.price_type}
                    onValueChange={(value) => handleChange("price_type", value)}
                  >
                    <SelectTrigger className="mt-1 h-9 rounded-lg bg-white border-slate-200 hover:border-slate-300 focus:ring-indigo-500 transition-all shadow-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fixed">Fijo ($)</SelectItem>
                      <SelectItem value="percentage">Porcentaje (%)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex items-center justify-between border border-slate-200/60 p-3 rounded-xl bg-white shadow-sm">
                <div className="space-y-0.5">
                  <Label htmlFor="is_required" className="text-sm font-medium text-slate-800">Ajuste Obligatorio</Label>
                  <p className="text-xs text-slate-500">
                    Se aplicará automáticamente a este producto
                  </p>
                </div>
                <Switch
                  id="is_required"
                  checked={formData.is_required}
                  onCheckedChange={(checked) =>
                    handleChange("is_required", checked)
                  }
                  className="data-[state=checked]:bg-indigo-600"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description" className="text-sm font-medium text-slate-700">Descripción (Opcional)</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => handleChange("description", e.target.value)}
                  placeholder="Detalles sobre este ajuste..."
                  rows={3}
                  className="mt-1 rounded-lg bg-white border-slate-200 hover:border-slate-300 focus-visible:ring-indigo-500 focus-visible:border-indigo-500 transition-all shadow-sm"
                />
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-white border-t border-slate-100 p-3 sm:px-5 sm:py-3 flex-shrink-0">
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isLoading}
                className="rounded-xl border-slate-200 hover:bg-slate-50 text-slate-700 font-medium transition-all h-9 px-4 text-sm"
              >
                Cancelar
              </Button>
              <Button 
                type="submit" 
                disabled={isLoading}
                className="bg-slate-900 hover:bg-black text-white rounded-xl shadow-lg shadow-slate-900/20 font-medium transition-all h-9 px-5 text-sm"
              >
                {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {editingRule ? "Guardar Cambios" : "Crear Ajuste"}
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
